<template>
    <div v-if="loaded">
        <rkm-data-table
            :repository="repositories.fila"
            :request-params="camelObjectKeysToSnakeRecursive(filters)"
            :fields="fields"
            :tbody-tr-class="rowClass"
            class="fila"
            ref="DataTable"
            sort-by="posicaoRegulador"
        >
            <template v-slot:filters>
                <rkm-form
                    ref="Form"
                    class="mb-2"
                >
                    <rkm-row>
                        <rkm-col cols="4">
                            <rkm-form-date-picker
                                :end-validations="validations.periodo"
                                :max-date="additionals.dataAtual"
                                :range-inline="false"
                                :start-validations="validations.periodo"
                                end-label="Até"
                                range
                                start-label="Período de"
                                v-model="model.periodo"
                            />
                        </rkm-col>

                        <rkm-col cols="8">
                            <rkm-form-input-autocomplete
                                :repository="repositories.fila.paciente"
                                :request-query-string-params="filters"
                                :search-fields="pacienteSearchFields"
                                :text-field="p => `${p.nome}${p.cns ? ` - ${p.cns}` : ''}`"
                                label="Paciente"
                                v-model="model.paciente"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">
                            <rkm-form-select
                                :options="form.unidadesReguladoras"
                                :validations="validations.unidadeReguladora"
                                @change="loadFrom('unidade_reguladora')"
                                label="Unidade reguladora"
                                show-default-option
                                text-field="nomeFantasia"
                                v-model="model.unidadeReguladoraId"
                                value-field='id'
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                :disabled="!model.unidadeReguladoraId"
                                :options="form.reguladores"
                                :validations="validations.regulador"
                                @change="loadFrom('regulador')"
                                label="Local de atendimento regulador"
                                show-default-option
                                text-field="nome"
                                v-model="model.reguladorId"
                                value-field='id'
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                :options="form.unidadesSolicitantes"
                                @change="loadFrom('unidade_solicitante')"
                                default-option-text="Todas"
                                label="Unidade solicitante"
                                show-default-option
                                text-field="nomeFantasia"
                                v-model="model.unidadeSolicitanteId"
                                value-field='id'
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">
                            <rkm-form-select
                                :disabled="!model.unidadeSolicitanteId"
                                :options="form.infraestruturasSolicitantes"
                                @change="loadFrom('infraestrutura_solicitante')"
                                default-option-text="Todos"
                                label="Local de atendimento solicitante"
                                show-default-option
                                text-field="nome"
                                v-model="model.infraestruturaSolicitanteId"
                                value-field='id'
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                :options="form.usuariosSolicitantes"
                                @change="loadFrom('usuario_solicitante')"
                                default-option-text="Todos"
                                label="Profissional solicitante"
                                show-default-option
                                text-field="nome"
                                v-model="model.usuarioSolicitanteId"
                                value-field='id'
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-select
                                :options="form.prestadores"
                                @change="loadFrom('prestador')"
                                default-option-text="Todos"
                                label="Prestadores"
                                show-default-option
                                text-field="nome"
                                v-model="model.prestador"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="2">
                            <rkm-form-multiselect
                                :options="form.status"
                                :select-limit="null"
                                @update:dropdownClosed="loadFrom('status')"
                                label="Status"
                                option-name-key="nome"
                                option-text-selecteds="status selecionado"
                                v-model="model.status"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="2">
                            <rkm-form-select
                                :options="form.opcoesRetiradaProtocoloAgendamento"
                                @change="loadFrom('opcao_retirada_protocolo_agendamento')"
                                default-option-text="Todos"
                                label="Retirada de protocolo"
                                show-default-option
                                text-field="nome"
                                v-model="model.opcaoRetiradaProtocoloAgendamentoId"
                                value-field='id'
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-multiselect
                                :options="form.grupos"
                                :select-limit="null"
                                @update:dropdownClosed="loadFrom('grupo')"
                                label="Grupo"
                                option-name-key="nome"
                                option-text-selecteds="grupo"
                                v-model="model.grupos"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-multiselect
                                :options="form.exames"
                                :select-limit="null"
                                label="Exame"
                                option-name-key="descricao"
                                option-text-selecteds="exame"
                                v-model="model.exames"
                                value-field="id"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">
                            <rkm-form-multiselect
                                :options="form.procedimentosCirurgicos"
                                :select-limit="null"
                                label="Procedimento cirúrgico"
                                option-name-key="descricao"
                                option-text-selecteds="procedimento cirúrgico"
                                v-model="model.procedimentosCirurgicos"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-multiselect
                                :options="form.especialidades"
                                :select-limit="null"
                                label="Especialidade"
                                option-name-key="descricao"
                                option-text-selecteds="especialidade"
                                v-model="model.especialidades"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="auto">
                            <rkm-button
                                :label="false"
                                @click="$refs.Form.validate($refs.DataTable.search)"
                                color="primary"
                            >
                                Buscar
                            </rkm-button>

                            <rkm-button
                                :label="false"
                                @click="resetFilters"
                            >
                                Limpar
                            </rkm-button>
                        </rkm-col>
                    </rkm-row>
                </rkm-form>
            </template>

            <template v-slot:cell(itens)="{ value }">
                <div
                    v-for="item in value"
                    :key="item"
                >
                    {{ item }}
                </div>
            </template>

            <template v-slot:cell(paciente)="{ item }">
                <rkm-button
                    :id="uniqueId(`paciente-icon-${item.id}`)"
                    small
                >
                    <rkm-icon icon="user"/>
                </rkm-button>

                <rkm-popover
                    :target="uniqueId(`paciente-icon-${item.id}`)"
                    triggers="hover"
                    placement="top"
                    title="Dados cadastrais"
                >
                    <div>
                        <span>
                            <strong>Idade:</strong> {{ item.idade }}
                        </span>
                    </div>

                    <div>
                        <span>
                            <strong>Sexo:</strong> {{ item.sexoDescricao }}
                        </span>
                    </div>

                    <div>
                        <span>
                            <strong>Prontuário:</strong> {{ item.prontuario }}
                        </span>
                    </div>
                </rkm-popover>

                {{ item.pacienteNome }}
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-dropdown
                    color="primary"
                    position="left"
                    no-caret
                    title="Ações"
                    small
                >
                    <template #button-content>
                        <rkm-icon icon="bars" />
                    </template>

                    <rkm-dropdown-item @click="$refs.ModalDetalhe.open(item)">Exibir detalhes</rkm-dropdown-item>
                    <rkm-dropdown-item @click="$refs.ModalImpressao.open(item)">Impressões</rkm-dropdown-item>
                    <rkm-dropdown-item @click="$refs.ModalTentativaContato.open(item)">Tentativas de contato</rkm-dropdown-item>
                    <rkm-dropdown-item @click="$refs.ModalSolicitacao.open(item)">
                        {{ item.allowUpdate ? 'Atualizar' : 'Visualizar' }}
                    </rkm-dropdown-item>
                </rkm-dropdown>
            </template>

            <template #afterTable>
                <div class="my-2">
                    <p>A linha em <strong class="purple">roxo</strong> representa que o paciente está em óbito.</p>
                </div>
            </template>
        </rkm-data-table>

        <ModalDetalhe ref="ModalDetalhe" />
        <ModalImpressao ref="ModalImpressao" />
        <ModalTentativaContato ref="ModalTentativaContato" />

        <ModalSolicitacao
            @save="loadFromPrestadorAfterUpdateSolicitacao"
            ref="ModalSolicitacao"
        />
    </div>
</template>

<script>
// Components.
import ModalDetalhe from './components/detalhe/Modal'
import ModalImpressao from './components/impressao/Modal'
import ModalTentativaContato from './components/tentativa-contato/Modal'
import ModalSolicitacao from './components/solicitacao/Modal'

// Mixins.
import { repository, error, stringCase, object } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

// Enums.
import statusTipo from '@/enums/regulacao/statusTipo'

// Moment.
import moment from 'moment'

export default {
    components: {
        ModalDetalhe,
        ModalImpressao,
        ModalTentativaContato,
        ModalSolicitacao,
    },
    computed: {
        validations() {

            let isPeriodoRequired = !(!!this.model.paciente
                || (this.model.status.length > 0 && this.model.grupos.length > 0)
                || (this.model.status.length > 0 && (this.model.exames.length > 0
                    || this.model.procedimentosCirurgicos.length > 0
                    || this.model.especialidades.length > 0)))

            return {
                periodo: { required: isPeriodoRequired },
                unidadeReguladora: { required: true },
                regulador: { required: true },
            }
        },
        filters() {

            return this.camelObjectKeysToSnakeRecursive({
                periodo: {
                    start: this.model.periodo.start ? moment(this.model.periodo.start).format('DD/MM/YYYY') : null,
                    end: this.model.periodo.end ? moment(this.model.periodo.end).format('DD/MM/YYYY') : null,
                },
                pacienteId: this.model.paciente?.id ?? null,
                unidadeReguladoraId: this.model.unidadeReguladoraId,
                reguladorId: this.model.reguladorId,
                unidadeSolicitanteId: this.model.unidadeSolicitanteId,
                infraestruturaSolicitanteId: this.model.infraestruturaSolicitanteId,
                usuarioSolicitanteId: this.model.usuarioSolicitanteId,
                prestador: this.model.prestador,
                status: this.model.status,
                opcaoRetiradaProtocoloAgendamentoId: this.model.opcaoRetiradaProtocoloAgendamentoId,
                grupos: this.model.grupos,
                exames: this.model.exames,
                procedimentosCirurgicos: this.model.procedimentosCirurgicos,
                especialidades: this.model.especialidades,
            })
        },
        fields() {

            return [
                { key: 'data', name: 'solicitacao.data', label: 'Data da solicitação', sortable: true },
                { key: 'posicaoRegulador', name: 'solicitacao.posicaoRegulador', label: 'Posição', sortable: true },
                { key: 'itens', label: 'Procedimento(s) / Especialidade' },
                { key: 'paciente', name: 'pacienteCidadao.nome', label: 'Paciente', sortable: true },
                { key: 'cidDescricao', name: 'cidDescricao', label: 'CID', sortable: true },
                { key: 'statusNome', name: 'status.nome', label: 'Status', sortable: true },
                { key: 'tempoEspera', name: 'tempoEspera', label: 'Tempo de espera', sortable: true },
                { key: 'prestadorDescricao', name: 'prestadorDescricao', label: 'Prestador', sortable: true },
                { key: 'actions', label: 'Ações', class: 'text-center text-nowrap' },
            ]
        },
        pacienteSearchFields() {

            return [
                { key: 'cidadao.nome', searchable: true },
                { key: 'cidadao.cns', searchable: true },
                { key: 'cidadao.cpf', searchable: true },
                { key: 'cidadao.data_nascimento', searchable: true },
                { key: 'paciente.prontuario', searchable: true },
            ]
        }
    },
    created() {

        this.repositories = {
            fila: this.getConectaSusRepository('regulacao.fila')
        }

        this.load()
    },
    data: () => ({
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        enums: { statusTipo },
        unwatchers: {
            unwatchPeriodo: null,
            unwatchPaciente: null,
        },
        others: { moment },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.fila.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.unwatchers.unwatchPeriodo = this.$watch('model.periodo', {
                deep: true,
                handler() {

                    this.loadFrom('periodo')
                }
            })

            this.unwatchers.unwatchPaciente = this.$watch('model.paciente', {
                deep: true,
                handler() {

                    this.loadFrom('paciente')
                }
            })
        },
        async loadFromPrestadorAfterUpdateSolicitacao() {

            this.setLoading()

            try {

                const oldForm = this.clone(this.form)
                const oldModel = this.clone(this.model)

                const wasPrestadorTodos = oldModel.prestador === null
                const wasStatusTodos = oldModel.status.length === oldForm.status.length
                const wasOpcaoRetiradaProtocoloAgendamentoTodos = oldModel.opcaoRetiradaProtocoloAgendamentoId === null
                const wasGrupoTodos = oldModel.grupos.length === oldForm.grupos.length
                const wasExameTodos = oldModel.exames.length === oldForm.exames.length
                const wasProcedimentoCirugicoTodos = oldModel.procedimentosCirurgicos.length === oldForm.procedimentosCirurgicos.length
                const wasEspecialidadeTodos = oldModel.especialidades.length === oldForm.especialidades.length

                await this.listFrom('usuario_solicitante')

                if (!wasPrestadorTodos && this.form.prestadores.find(p => p.value === oldModel.prestador)) {

                    this.model.prestador = oldModel.prestador

                    await this.listFrom('prestador')
                }

                if (!wasStatusTodos) {

                    this.model.status = oldModel.status.filter(id => this.form.status.find(s => s.id === id))

                    await this.listFrom('status')
                }

                if (!wasOpcaoRetiradaProtocoloAgendamentoTodos && this.form.opcoesRetiradaProtocoloAgendamento
                    .find(o => o.id === oldModel.opcaoRetiradaProtocoloAgendamentoId)
                ) {

                    this.model.opcaoRetiradaProtocoloAgendamentoId = oldModel.opcaoRetiradaProtocoloAgendamentoId

                    await this.listFrom('opcao_retirada_protocolo_agendamento')
                }

                if (!wasGrupoTodos) {

                    this.model.grupos = oldModel.grupos.filter(id => this.form.grupos.find(g => g.id === id))

                    await this.listFrom('grupo')
                }

                if (!wasExameTodos) {

                    this.model.exames = oldModel.exames.filter(id => this.form.exames.find(e => e.id === id))
                }

                if (!wasProcedimentoCirugicoTodos) {

                    this.model.procedimentosCirurgicos = oldModel.procedimentosCirurgicos
                        .filter(id => this.form.procedimentosCirurgicos.find(pc => pc.id === id))
                }

                if (!wasEspecialidadeTodos) {

                    this.model.especialidades = oldModel.especialidades
                        .filter(id => this.form.especialidades.find(e => e.id === id))
                }

                this.$nextTick().then(() => this.$refs.DataTable.search())
            }
            catch (error) {

                this.handleError(error)
            }
            finally {

                this.unsetLoading()
            }
        },
        loadFrom(field) {

            this.setLoading()

            this.listFrom(field)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        async listFrom(field) {

            const { data } = await this.repositories.fila.list({ ...this.filters, load_from: this.camelToSnake(field) })

            if ('form' in data) {

                Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
            }

            Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
        },
        resetFilters() {

            this.unwatchers.unwatchPeriodo()
            this.unwatchers.unwatchPaciente()
            this.$refs.Form.resetValidator()

            this.$nextTick().then(() => this.load())
        },
        rowClass(item, type) {

            if (type === 'row' && item) {

                if (item.pacienteEmObito) {

                    return 'table-purple'
                }
                else {

                    switch (item.statusTipoId) {

                        case this.enums.statusTipo.AGENDADO: {

                            return 'table-primary'
                        }
                        case this.enums.statusTipo.CANCELADO: {

                            return 'table-danger'
                        }
                        case this.enums.statusTipo.DEVOLVIDO: {

                            return 'table-warning'
                        }
                        case this.enums.statusTipo.REALIZADO: {

                            return 'table-success'
                        }
                        case this.enums.statusTipo.REAGENDADO: {

                            return 'table-orange'
                        }
                        default: {

                            return ''
                        }
                    }
                }
            }
        },
    },
    mixins: [repository, error, stringCase, object],
    watch: {
        model: {
            deep: true,
            handler() {

                this.$nextTick().then(() => this.$refs.DataTable.clearTable())
            }
        }
    }
}
</script>

<style scoped>
.fila >>> .purple {
    color: #733380;
}

.fila >>> .table-purple {
    border-color: #a275a9;
    background-color: #e8dbe9;
}

.fila >>> .table-orange {
    border-color: #ffb067;
    background-color: #ffd7b2;
}
</style>